import type { CSSObject } from '@emotion/react'
import {
  Color,
  Shadow,
  ShadowElevation,
  Size,
  Spacing,
  Timing,
  Typography,
} from '@opallabs/gem'
import { WithRequiredTheme } from '@opallabs/lapidary'

type StyleProps = Readonly<
  WithRequiredTheme & {
    selected: boolean
    clickable: boolean
  }
>

const getBackgroundColor = ({
  clickable,
  selected,
  theme,
}: StyleProps): string => {
  if (selected) return Color[theme].Translucent.A08
  if (!clickable) return Color[theme].Surface.N200

  return Color[theme].Surface.N000
}

export const card = ({
  selected,
  clickable,
  theme,
}: StyleProps): CSSObject => ({
  backgroundColor: getBackgroundColor({ selected, clickable, theme }),
  borderColor: selected ? Color[theme].Surface.A200 : 'transparent',
  color: Color[theme].Text.Default,
  ...Shadow[ShadowElevation.E1],
  borderStyle: 'solid',
  borderWidth: Spacing.XS / 2,
  borderRadius: Size.Rounding * 3,
  boxSizing: 'border-box',
  cursor: clickable ? 'pointer' : 'not-allowed',
  display: 'flex',
  flexDirection: 'column',
  blockSize: Spacing.M * 11.5,
  justifyContent: 'space-between',
  marginBlockEnd: Spacing.M * 1.75,
  opacity: !clickable ? 0.5 : 1,
  paddingBlockEnd: Spacing.M * 1.75,
  paddingBlockStart: Spacing.M,
  paddingInlineEnd: Spacing.M,
  paddingInlineStart: Spacing.L,
  position: 'relative',
  inlineSize: Spacing.M * 15.25,
  transitionDuration: `${Timing.Default}ms`,
  transitionProperty: ['border-color', 'box-shadow', 'color', 'opacity'].join(
    ', '
  ),
  transitionTimingFunction: 'ease-in-out',

  '&:focus-within, &:hover': {
    boxShadow: clickable
      ? Shadow[ShadowElevation.E2].boxShadow
      : Shadow[ShadowElevation.E1].boxShadow,
  },
  '&:nth-of-type(n + 4)': {
    alignSelf: 'flex-end',
  },

  '@media (prefers-reduced-motion: reduced)': {
    transition: 'none',
  },
})

export const decoration: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
}

export const description: CSSObject = {
  ...Typography.H100,
  cursor: 'inherit',
}

export const checkbox: CSSObject = {
  marginInlineStart: 'auto',
}

export const icon: CSSObject = {
  cursor: 'inherit',
}
